import React from 'react';

import { Formik, FieldArray } from 'formik';
import * as Yup from 'yup';

import { StatusSelector, ImpactCategorySelector, GeneralRisksSelector, GeneralIssuesSelector, VenueSelector } from '../Selectors';

import Button from 'CommonComponents/Button';
import DatePickerField from 'CommonComponents/DatePickerField';
import ModalFooter from 'CommonComponents/ModalFooter';
import SelectFromField from 'CommonComponents/SelectFromField';
import TextareaFormField from 'CommonComponents/TextareaFormField';
import TextFormField from 'CommonComponents/TextFormField';
import UserSelectorInput from 'CommonComponents/UserSelectorInput';

import { useUsers } from 'Hooks/useUsers';

import MinusIcon from 'Assets/icons/minus.svg';
import PlusIcon from 'Assets/icons/plus.svg';

const PRIORITIES = ['low', 'medium', 'high'];
const DEFAULT_ACTION = { id: null, description: '', owner_id: '', date: '', completed_date: '' };
const GeneralIssueSchema = Yup.object().shape({
    description: Yup.string().required($t('This value is required')),
    detailed_description: Yup.string(),
    owner_id: Yup.string(),
    target_date: Yup.date().nullable(),
    date: Yup.date().nullable(),
    forum: Yup.string(),
    outcome: Yup.string().nullable(),
    priority: Yup.string().oneOf(PRIORITIES),
    general_status_id: Yup.string(),
    impact_category_ids: Yup.array(Yup.string()),
    general_risk_ids: Yup.array(Yup.string()),
    general_issue_ids: Yup.array(Yup.string()),
    venue_ids: Yup.array().of(Yup.string()),
    actions: Yup.array().of(
        Yup.object().shape({
            description: Yup.string().required($t('This value is required')),
            owner_id: Yup.string(),
            date: Yup.date().nullable(),
            completed_date: Yup.date().nullable(),
        }),
    ),
});

export default function GeneralDecisionForm(props) {
    const { users } = useUsers();

    return (
        <Formik
            enableReinitialize
            initialValues={{
                description: props.decision?.description || '',
                detailed_description: props.decision?.detailed_description || '',
                owner_id: props.decision?.owner_id || '',
                target_date: props.decision?.target_date || '',
                date: props.decision?.date || '',
                forum: props.decision?.forum || '',
                outcome: props.decision?.outcome || '',
                priority: props.decision?.priority || '',
                general_status_id: props.decision?.general_status_id || '',
                impact_category_ids: props.decision?.impact_category_ids?.map(String) || [],
                general_risk_ids: props.decision?.general_risk_ids?.map(String) || [],
                general_issue_ids: props.decision?.general_issue_ids?.map(String) || [],
                venue_ids: props.decision?.venue_ids?.map(String) || [],
                actions:
                    props.decision?.actions?.map((action) => ({
                        ...action,
                        description: action.description ?? DEFAULT_ACTION.description,
                        owner_id: action.owner_id ?? DEFAULT_ACTION.owner_id,
                        date: action.date ?? DEFAULT_ACTION.date,
                    })) || [],
            }}
            validationSchema={GeneralIssueSchema}
            onSubmit={(values, actions) => {
                if (props.decision?.id) {
                    if (props.currentVenue) {
                        // if the decision is being edited from a venue context, we want to keep the decision associated to the current venue only, as we don't want the user to edit the venues from this form
                        values.venue_ids = props.decision?.venue_ids?.map(String) || [];
                    }

                    const updateUrl = props.updateUrl.replace('GENERAL_DECISION_ID', props.decision.id);

                    axios
                        .put(updateUrl, values)
                        .then((res) => {
                            actions.resetForm();
                            props.onSuccess();
                        })
                        .catch((e) => {
                            e?.response?.data?.errors && actions.setErrors(e.response.data.errors);
                        })
                        .finally(() => {
                            actions.setSubmitting(false);
                        });
                } else {
                    if (props.currentVenue) {
                        // if the decision is being created from a venue context, we want to automatically associate the decision to the current venue, as we don't want the user to select the venues from this form
                        values.venue_ids = [props.currentVenue.id];
                    }

                    axios
                        .post(props.storeUrl, values)
                        .then((res) => {
                            actions.resetForm();
                            props.onSuccess();
                        })
                        .catch((e) => {
                            e?.response?.data?.errors && actions.setErrors(e?.response?.data?.errors);
                        })
                        .finally(() => {
                            actions.setSubmitting(false);
                        });
                }
            }}
        >
            {({ values, errors, touched, isSubmitting, handleSubmit, setFieldValue }) => (
                <>
                    <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                        <TextFormField
                            label={$t('Description')}
                            required={true}
                            type="text"
                            name="description"
                            placeholder={$t('Enter decision description')}
                        ></TextFormField>
                        <div className="tw-flex tw-flex-col tw-space-y-1.5">
                            <label className="text-input__label">{$t('Owner')}</label>
                            <UserSelectorInput
                                selectedId={values.owner_id}
                                onChange={(value) => setFieldValue('owner_id', value)}
                                usersList={users}
                                fetchIfNoUsers={false}
                            />
                            {errors.owner_id && touched.owner_id ? <div className="tw-text-red-500 tw-text-sm">{errors.owner_id}</div> : null}
                        </div>
                    </div>

                    <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                        <SelectFromField label={$t('Priority')} as="select" name="priority">
                            <option>{$t('Select a priority')}</option>
                            {PRIORITIES.map((priority) => (
                                <option key={priority} value={priority} className="text-capitalize">
                                    {priority.charAt(0).toUpperCase() + priority.slice(1)}
                                </option>
                            ))}
                        </SelectFromField>
                        <StatusSelector
                            name="general_status_id"
                            statuses={props.datasets.statuses}
                            errors={errors.general_status_id}
                            touched={touched.general_status_id}
                        />
                    </div>

                    {props.allowVenueSelection && (
                        <div className="tw-grid tw-grid-cols-1">
                            <VenueSelector
                                venuesOptions={props.datasets.venuesOptions}
                                values={values}
                                onChange={(venues) => setFieldValue('venue_ids', venues?.map((venue) => venue.value) || [])}
                                disabled={isSubmitting}
                                errors={errors.venue_ids}
                                touched={touched.venue_ids}
                            />
                        </div>
                    )}

                    <div className="tw-grid tw-grid-cols-1">
                        <TextareaFormField
                            label={$t('Detailed description')}
                            as="textarea"
                            type="text"
                            name="detailed_description"
                            placeholder={$t("Enter decision's detailed description")}
                        ></TextareaFormField>
                    </div>

                    <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                        <div className="tw-flex tw-flex-col tw-space-y-1.5">
                            <label className="tw-text-charcoal tw-text-sm tw-mb-0" htmlFor="target_date">
                                {$t('Target Decision Date')}
                            </label>
                            <DatePickerField
                                name="target_date"
                                valueFormat="yyyy-MM-dd"
                                dateFormat="yyyy-MM-dd"
                                className={errors.target_date && touched.target_date ? 'tw-border tw-border-red-500 text-input' : 'tw-border-0 text-input'}
                                value={values.target_date}
                                onChange={setFieldValue}
                            />
                            {errors.target_date && touched.target_date && <div className="tw-text-red-500 tw-text-sm">{errors.target_date}</div>}
                        </div>
                        <ImpactCategorySelector
                            name="impact_category_ids"
                            isSpanToTwo={true}
                            categories={props.datasets.impactCategories}
                            errors={errors.impact_category_ids}
                            touched={touched.impact_category_ids}
                        />
                    </div>

                    <div className="tw-w-full tw-border-b tw-border-2 tw-border-grey"></div>

                    <FieldArray
                        name="actions"
                        render={(arrayHelpers) => (
                            <>
                                {!!values.actions?.length &&
                                    values.actions.map((action, index) => {
                                        const actionErrors = errors.actions && errors.actions[index];
                                        const actionTouched = touched.actions && touched.actions[index];
                                        return (
                                            <div
                                                className="tw-flex tw-flex-col sm:tw-flex-row tw-space-y-4 sm:tw-space-y-0 tw-space-x-0 sm:tw-space-x-4"
                                                key={index}
                                            >
                                                <div className="tw-w-full">
                                                    <TextareaFormField
                                                        label={$t('Action')}
                                                        required={true}
                                                        as="textarea"
                                                        name={`actions.${index}.description`}
                                                        placeholder={$t('Enter action description')}
                                                    ></TextareaFormField>
                                                </div>
                                                <div className="tw-grid tw-grid-rows-2 tw-gap-4 tw-w-ull">
                                                    <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                                                        <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                                            <label className="text-input__label">{$t('Owner')}</label>
                                                            <UserSelectorInput
                                                                selectedId={action.owner_id}
                                                                onChange={(value) => setFieldValue(`actions.${index}.owner_id`, value)}
                                                                usersList={users}
                                                                fetchIfNoUsers={false}
                                                            />
                                                            {actionErrors?.owner_id && actionTouched?.owner_id ? (
                                                                <div className="tw-text-red-500 tw-text-sm">{actionErrors?.owner_id}</div>
                                                            ) : null}
                                                        </div>
                                                        <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                                            <label className="text-input__label">{$t('Due Date')}</label>
                                                            <DatePickerField
                                                                name={`actions.${index}.date`}
                                                                valueFormat="yyyy-MM-dd"
                                                                dateFormat="yyyy-MM-dd"
                                                                className={
                                                                    actionErrors?.date && actionTouched?.date
                                                                        ? 'tw-border tw-border-red-500 text-input'
                                                                        : 'tw-border-0 text-input'
                                                                }
                                                                value={values?.actions[index]?.date}
                                                                onChange={setFieldValue}
                                                            />
                                                            {actionErrors?.date && actionTouched?.date && (
                                                                <div className="tw-text-red-500 tw-text-sm">{actionErrors?.date}</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="tw-grid tw-grid-cols-1">
                                                        <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                                            <label className="text-input__label">{$t('Completed')}</label>
                                                            <DatePickerField
                                                                name={`actions.${index}.completed_date`}
                                                                className={
                                                                    actionErrors?.completed_date && actionTouched?.completed_date
                                                                        ? 'tw-border tw-border-red-500 text-input'
                                                                        : 'tw-border-0 text-input'
                                                                }
                                                                value={values?.actions[index]?.completed_date}
                                                                onChange={setFieldValue}
                                                            />
                                                            {actionErrors?.completed_date && actionTouched?.completed_date && (
                                                                <div className="tw-text-red-500 tw-text-sm">{actionErrors?.completed_date}</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="">
                                                    <Button small variant="danger" className="mt-4" onClick={() => arrayHelpers.remove(index)}>
                                                        <MinusIcon className="tw-w-3.5 tw-h-3.5" />
                                                    </Button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                <div className="tw-flex">
                                    <Button small variant="primary" onClick={() => arrayHelpers.push(DEFAULT_ACTION)}>
                                        <PlusIcon className="tw-w-3 tw-h-3" aria-hidden="true" />
                                        {$t('Add new action')}
                                    </Button>
                                </div>
                            </>
                        )}
                    />

                    <div className="tw-w-full tw-border-b tw-border-2 tw-border-grey"></div>

                    <div className="tw-grid tw-grid-cols-1">
                        <TextareaFormField label={$t('Decision Outcome')} as="textarea" type="text" name="outcome"></TextareaFormField>
                    </div>

                    <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                        <div className="tw-flex tw-flex-col tw-space-y-1.5">
                            <label className="text-input__label" htmlFor="date">
                                {$t('Date Decision Made')}
                            </label>
                            <DatePickerField
                                name="date"
                                valueFormat="yyyy-MM-dd"
                                dateFormat="yyyy-MM-dd"
                                className={errors.date && touched.date ? 'tw-border tw-border-red-500 text-input' : 'tw-border-0 text-input'}
                                value={values.date}
                                onChange={setFieldValue}
                            />
                            {errors.date && touched.date && <div className="tw-text-red-500 tw-text-sm">{errors.date}</div>}
                        </div>
                        <TextFormField label={$t('Decision Forum')} type="text" name="forum"></TextFormField>
                    </div>

                    <div className="tw-w-full tw-border-b tw-border-2 tw-border-grey"></div>

                    <div className="tw-grid tw-grid-cols-1 tw-gap-4">
                        <h6 className="tw-text-xl tw-text-charcoal tw-font-semibold">{$t('Inter-dependencies')}</h6>
                        <GeneralRisksSelector
                            name="general_risk_ids"
                            risks={props.datasets.risks}
                            errors={errors.general_risk_ids}
                            touched={touched.general_risk_ids}
                        />
                        <GeneralIssuesSelector
                            name="general_issue_ids"
                            issues={props.datasets.issues}
                            errors={errors.general_issue_ids}
                            touched={touched.general_issue_ids}
                        />
                    </div>

                    <ModalFooter
                        cancelButtonText={$t('Cancel')}
                        primaryButtonText={$t('Save')}
                        onCancel={props.onCancel}
                        handleSubmit={handleSubmit}
                        isSubmitting={isSubmitting}
                    ></ModalFooter>
                </>
            )}
        </Formik>
    );
}
